import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Activity, BookOpen, GraduationCap, Pill } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Btn, Disclaimer, Loading, PageHero, SectionHead } from "@/components/public/Ui";
import NotFound from "@/pages/public/NotFound";

const ICONS = { pill: Pill, "book-open": BookOpen, activity: Activity, "graduation-cap": GraduationCap };

export function ProgramsIndex() {
  const [page, setPage] = useState(null);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/public/pages/our-efforts").then((r) => setPage(r.data)).catch(() => setPage(false));
    api.get("/public/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  if (page === null) return <Loading />;
  const hero = page?.body_json?.hero || {};

  return (
    <>
      <Seo title={page?.seo_title} description={page?.seo_description} canonicalPath="/our-efforts" />
      <PageHero
        overline={hero.overline}
        line1={hero.heading_line_1}
        line2={hero.heading_line_2}
        subheading={hero.subheading}
        image={mediaUrl(page?.hero_image_url)}
      />
      <div className="bka-container py-14 lg:py-20" data-testid="programs-index">
        <SectionHead overline="Programs" title="What We Do Across The Islands" />
        <div className="grid gap-6 md:grid-cols-2">
          {programs.map((p) => {
            const Icon = ICONS[p.icon] || Activity;
            return (
              <Link
                key={p.id}
                to={`/programs/${p.slug}`}
                data-testid={`program-card-${p.slug}`}
                className="bka-card-lift group grid overflow-hidden rounded-3xl border border-slate-200 bg-white sm:grid-cols-[0.7fr_1fr]"
              >
                <div className="h-40 overflow-hidden sm:h-full">
                  <img
                    src={mediaUrl(p.image_url)}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 lg:p-7">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bka-aqua text-bka-emerald">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold leading-snug text-bka-navy">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-bka-ink/75">{p.summary}</p>
                  <span className="mt-5 inline-block text-sm font-bold text-bka-emerald">Learn more →</span>
                </div>
              </Link>
            );
          })}
        </div>
        {page?.body_json?.note && (
          <div className="mt-12">
            <Disclaimer title="Please Note" body={page.body_json.note} testId="programs-note" />
          </div>
        )}
      </div>
    </>
  );
}

export function ProgramDetail() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);

  useEffect(() => {
    setProgram(null);
    api.get(`/public/programs/${slug}`).then((r) => setProgram(r.data)).catch(() => setProgram(false));
  }, [slug]);

  if (program === null) return <Loading />;
  if (program === false) return <NotFound />;

  return (
    <>
      <Seo
        title={`${program.title} | Bahamas Kidney Association`}
        description={program.summary}
        canonicalPath={`/programs/${slug}`}
      />
      <PageHero overline="Our Efforts" line1={program.title} subheading={program.summary} image={mediaUrl(program.image_url)} />
      <div className="bka-container max-w-3xl py-14 lg:py-20" data-testid={`program-${slug}`}>
        <div className="bka-prose">
          {String(program.body || "")
            .split("\n\n")
            .map((p, i) => (
              <p key={i}>{p}</p>
            ))}
        </div>
        <div className="mt-12">
          <Disclaimer
            title="Please Note"
            body="Program availability, the medicines covered, eligibility requirements and capacity can change. Please contact us to confirm current details before making plans."
          />
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Btn to="/get-support" testId="program-get-support">
            Get Support
          </Btn>
          <Btn to="/our-efforts" variant="outline" testId="program-back">
            All Programs
          </Btn>
        </div>
      </div>
    </>
  );
}
